// AlarmTab — alarm manager ported from Apps/Yawmi. Storage: aweni_yawmi_alarms.
import type { AweniContext } from '../../types';

interface Alarm {
  id: string; time: string; label: string;
  repeat: 'daily' | 'weekdays' | 'weekend' | 'custom' | 'once';
  days?: number[]; date?: string; enabled: boolean;
  lastTriggered?: string; snoozedUntil?: number;
}

const STORAGE_KEY = 'aweni_yawmi_alarms';
const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

export class AlarmTab {
  private el: HTMLElement;
  private alarms: Alarm[] = [];
  private checker: number | null = null;
  private showForm = false;
  private firingId: string | null = null;

  constructor(container: HTMLElement) { this.el = container; }

  mount(ctx: AweniContext): void {
    try { this.alarms = JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]'); } catch { this.alarms = []; }
    this.render(ctx);
    this.checker = window.setInterval(() => this.check(ctx), 30000);
  }

  unmount(): void {
    if (this.checker !== null) { clearInterval(this.checker); this.checker = null; }
    this.el.innerHTML = '';
  }

  private tr(ctx: AweniContext, k: string, fb: string): string { return ctx.i18n.t(k) || fb; }

  private save(): void { localStorage.setItem(STORAGE_KEY, JSON.stringify(this.alarms)); }

  private add(alarm: Omit<Alarm, 'id'>): void {
    this.alarms.push({ ...alarm, id: `alarm_${Date.now()}` });
    this.save();
  }

  private toggle(id: string): void {
    const a = this.alarms.find(a => a.id === id);
    if (a) { a.enabled = !a.enabled; this.save(); }
  }

  private delete(id: string): void {
    this.alarms = this.alarms.filter(a => a.id !== id);
    this.save();
  }

  private shouldFire(alarm: Alarm): boolean {
    if (!alarm.enabled) return false;
    const now = new Date();
    const [h, m] = alarm.time.split(':').map(Number);
    if (now.getHours() !== h || now.getMinutes() !== m) return false;
    const key = `${now.toISOString().slice(0, 16)}`;
    if (alarm.lastTriggered === key) return false;
    if (alarm.snoozedUntil && Date.now() < alarm.snoozedUntil) return false;
    const dow = now.getDay();
    switch (alarm.repeat) {
      case 'daily': return true;
      case 'weekdays': return dow >= 1 && dow <= 5;
      case 'weekend': return dow === 0 || dow === 6;
      case 'custom': return (alarm.days ?? []).includes(dow);
      case 'once': return alarm.date === now.toISOString().slice(0, 10);
    }
  }

  private check(ctx: AweniContext): void {
    for (const a of this.alarms) {
      if (this.shouldFire(a)) {
        const now = new Date();
        a.lastTriggered = now.toISOString().slice(0, 16);
        this.save();
        this.firingId = a.id;
        this.render(ctx);
        if (Notification.permission === 'granted') {
          new Notification(a.label || this.tr(ctx, 'yawmi_alarms_untitled', 'Alarm'), {
            body: this.tr(ctx, 'yawmi_alarms_notify_body', 'Alarm is ringing'),
          });
        }
        break;
      }
    }
  }

  private render(ctx: AweniContext): void {
    const t = (k: string, fb: string) => this.tr(ctx, k, fb);
    const sorted = [...this.alarms].sort((a, b) => a.time.localeCompare(b.time));

    // Firing overlay
    if (this.firingId) {
      const a = this.alarms.find(a => a.id === this.firingId);
      if (a) {
        this.el.innerHTML = `
          <div style="text-align:center;padding:48px 16px;background:var(--ln-bg-card);border-radius:var(--ln-radius-lg);margin:16px 0">
            <div style="font-size:3rem;margin-bottom:12px">⏰</div>
            <div style="font-size:1.3rem;font-weight:700;margin-bottom:4px">${a.label || t('yawmi_alarms_untitled', 'Alarm')}</div>
            <div style="font-size:2rem;font-weight:900;color:var(--ln-accent);margin-bottom:24px">${a.time}</div>
            <div style="display:flex;gap:12px;justify-content:center">
              <button id="alarm-dismiss" style="padding:12px 24px;background:var(--ln-accent);color:var(--ln-accent-text);border:none;border-radius:var(--ln-radius-md);cursor:pointer;font-weight:700">
                ${t('yawmi_alarms_dismiss', 'Dismiss')}</button>
              <button id="alarm-snooze" style="padding:12px 24px;background:var(--ln-bg-card);border:1px solid var(--ln-border);border-radius:var(--ln-radius-md);cursor:pointer">
                ${t('yawmi_alarms_snooze', 'Snooze 5 min')}</button>
            </div>
          </div>`;
        this.el.querySelector('#alarm-dismiss')?.addEventListener('click', () => { this.firingId = null; this.render(ctx); });
        this.el.querySelector('#alarm-snooze')?.addEventListener('click', () => {
          if (a) { a.snoozedUntil = Date.now() + 5 * 60000; this.save(); }
          this.firingId = null; this.render(ctx);
        });
        return;
      }
    }

    const notifBanner = Notification.permission === 'default' ? `
      <div style="display:flex;align-items:center;gap:10px;padding:10px 12px;background:#fef3c7;
        border-radius:var(--ln-radius-md);margin-bottom:12px;font-size:.85rem">
        <span>🔔</span>
        <span style="flex:1">${t('yawmi_alarms_notif_banner', 'Enable notifications for background alarms.')}</span>
        <button id="alarm-notif-enable" style="padding:4px 10px;background:var(--ln-accent);color:var(--ln-accent-text);border:none;border-radius:var(--ln-radius-sm);cursor:pointer;font-size:.8rem">
          ${t('yawmi_alarms_notif_enable', 'Enable')}</button>
      </div>` : '';

    if (this.showForm) {
      this.el.innerHTML = `${notifBanner}
        <div style="background:var(--ln-bg-card);border-radius:var(--ln-radius-lg);padding:16px;margin-bottom:16px">
          <h3 style="margin:0 0 12px;font-size:1rem">${t('yawmi_alarms_add_title', 'New Alarm')}</h3>
          <div style="margin-bottom:10px">
            <label style="display:block;font-size:.85rem;margin-bottom:4px">${t('yawmi_alarms_time', 'Time')}</label>
            <input id="al-time" type="time" value="07:00" style="padding:8px;border:1px solid var(--ln-border);border-radius:var(--ln-radius-sm);width:100%;font-size:1rem">
          </div>
          <div style="margin-bottom:10px">
            <label style="display:block;font-size:.85rem;margin-bottom:4px">${t('yawmi_alarms_label', 'Label')}</label>
            <input id="al-label" type="text" placeholder="${t('yawmi_alarms_label_placeholder', 'e.g. Wake up...')}" style="padding:8px;border:1px solid var(--ln-border);border-radius:var(--ln-radius-sm);width:100%;font-size:.9rem">
          </div>
          <div style="margin-bottom:10px">
            <label style="display:block;font-size:.85rem;margin-bottom:4px">${t('yawmi_alarms_repeat', 'Repeat')}</label>
            <select id="al-repeat" style="padding:8px;border:1px solid var(--ln-border);border-radius:var(--ln-radius-sm);width:100%">
              ${(['daily','weekdays','weekend','once'] as const).map(r =>
                `<option value="${r}">${t('yawmi_alarms_repeat_' + r, r)}</option>`).join('')}
            </select>
          </div>
          <div style="display:flex;gap:10px;margin-top:14px">
            <button id="al-save" style="flex:1;padding:10px;background:var(--ln-accent);color:var(--ln-accent-text);border:none;border-radius:var(--ln-radius-md);cursor:pointer;font-weight:700">
              ${t('yawmi_alarms_save', 'Save Alarm')}</button>
            <button id="al-cancel" style="padding:10px 16px;background:transparent;border:1px solid var(--ln-border);border-radius:var(--ln-radius-md);cursor:pointer">
              ${t('yawmi_alarms_cancel', 'Cancel')}</button>
          </div>
        </div>`;
      this.el.querySelector('#al-save')?.addEventListener('click', () => {
        const time = (this.el.querySelector<HTMLInputElement>('#al-time'))?.value || '07:00';
        const label = (this.el.querySelector<HTMLInputElement>('#al-label'))?.value || '';
        const repeat = (this.el.querySelector<HTMLSelectElement>('#al-repeat'))?.value as Alarm['repeat'] || 'daily';
        this.add({ time, label, repeat, enabled: true });
        this.showForm = false;
        this.render(ctx);
      });
      this.el.querySelector('#al-cancel')?.addEventListener('click', () => { this.showForm = false; this.render(ctx); });
      this.el.querySelector('#alarm-notif-enable')?.addEventListener('click', () => Notification.requestPermission());
      return;
    }

    this.el.innerHTML = `${notifBanner}
      <div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:12px">
        <strong style="font-size:1rem">${t('yawmi_alarms_add_title', 'Alarms')}</strong>
        <button id="al-add-btn" style="padding:7px 14px;background:var(--ln-accent);color:var(--ln-accent-text);border:none;border-radius:var(--ln-radius-full);cursor:pointer;font-size:.85rem;font-weight:700">
          + ${t('yawmi_alarms_add', 'Add Alarm')}</button>
      </div>
      <p style="font-size:.75rem;color:var(--ln-text-secondary);margin-bottom:8px">${t('yawmi_alarms_web_note', 'Web alarms need the app open.')}</p>
      ${sorted.map(a => `
        <div style="display:flex;align-items:center;gap:10px;padding:12px;background:var(--ln-bg-card);
          border-radius:var(--ln-radius-md);margin-bottom:8px;opacity:${a.enabled ? '1' : '.5'}">
          <div style="flex:1">
            <div style="font-size:1.2rem;font-weight:700;color:var(--ln-accent)">${a.time}</div>
            <div style="font-size:.8rem;color:var(--ln-text-secondary)">${a.label || t('yawmi_alarms_untitled', 'Alarm')} · ${t('yawmi_alarms_repeat_' + a.repeat, a.repeat)}</div>
          </div>
          <label style="display:flex;align-items:center;cursor:pointer">
            <input type="checkbox" class="al-toggle" data-id="${a.id}" ${a.enabled ? 'checked' : ''} style="width:18px;height:18px;cursor:pointer">
          </label>
          <button class="al-del" data-id="${a.id}" style="padding:4px 8px;background:transparent;border:1px solid var(--ln-border);border-radius:var(--ln-radius-sm);cursor:pointer;font-size:.75rem">×</button>
        </div>`).join('')}
      ${!sorted.length ? `<p style="color:var(--ln-text-secondary);text-align:center;padding:24px 0">${t('yawmi_alarms_empty', 'No alarms yet.')}</p>` : ''}`;

    this.el.querySelector('#al-add-btn')?.addEventListener('click', () => { this.showForm = true; this.render(ctx); });
    this.el.querySelectorAll<HTMLInputElement>('.al-toggle').forEach(cb => {
      cb.addEventListener('change', () => { this.toggle(cb.dataset.id!); this.render(ctx); });
    });
    this.el.querySelectorAll<HTMLButtonElement>('.al-del').forEach(btn => {
      btn.addEventListener('click', () => {
        if (confirm(t('yawmi_alarms_delete_confirm', 'Delete this alarm?'))) { this.delete(btn.dataset.id!); this.render(ctx); }
      });
    });
    this.el.querySelector('#alarm-notif-enable')?.addEventListener('click', () => Notification.requestPermission().then(() => this.render(ctx)));
  }
}
